<template>
  <div class="login-main-wrapper">
    <div class="login-container">
      <div class="login-card shadow-lg">
        <div class="login-header text-center">
          <div class="brand-logo mb-0">
            <img src="@/assets/img/logo.png" alt="Sundava Logo" class="img-fluid" style="max-height: 120px;" />
          </div>
          <h4 class="fw-bold text-dark mt-n2 mb-1">Selamat Datang</h4>
          <p class="text-muted small">Silakan masuk ke akun Sundava Anda</p>
        </div>

        <Form @submit="onSubmit" :validation-schema="schema" v-slot="{ errors }" class="login-form mt-4">
          <div class="mb-3">
            <label class="form-label fw-semibold small text-uppercase" style="letter-spacing: 0.5px; color: #666;">Username</label>
            <div class="input-group-custom">
              <Field
                name="username"
                type="text"
                placeholder="Masukkan username"
                class="form-control minimal-input"
                :class="{ 'is-invalid': errors.username }"
              />
              <div class="invalid-feedback">{{ errors.username }}</div>
            </div>
          </div>

          <div class="mb-3">
            <div class="mb-2">
              <label class="form-label fw-semibold small text-uppercase" style="letter-spacing: 0.5px; color: #666;">Password</label>
            </div>
            <div class="input-group-custom position-relative">
              <Field
                name="password"
                :type="showPassword ? 'text' : 'password'"
                placeholder="Masukkan password"
                class="form-control minimal-input"
                :class="{ 'is-invalid': errors.password }"
              />
              <span @click="toggleShow" class="password-toggle-btn">
                <i :class="showPassword ? 'fas fa-eye' : 'fas fa-eye-slash'"></i>
              </span>
              <div class="invalid-feedback">{{ errors.password }}</div>
            </div>
          </div>



          <button type="submit" class="btn btn-login-custom w-100 py-2 fw-bold shadow-sm" :disabled="isSubmitting">
            <span v-if="!isSubmitting">Sign In</span>
            <span v-else class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
          </button>
        </Form>

        <div class="login-footer text-center mt-5">
           <p class="mb-0 text-muted" style="font-size: 11px;">
            &copy; {{ new Date().getFullYear() }} Sundava. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from "vue";
import { router } from "@/router";
import { Form, Field } from "vee-validate";
import * as Yup from "yup";
import Swal from "sweetalert2";
import authService from "@/services/authService";

export default {
  components: {
    Form,
    Field,
  },
  setup() {
    const showPassword = ref(false);
    const isSubmitting = ref(false);

    // Initialize local storage for demo if empty
    if (!localStorage.getItem("storedData")) {
      localStorage.setItem("storedData", JSON.stringify([{ email: "admin@sundava.com", password: "password" }]));
    }

    const schema = Yup.object().shape({
      username: Yup.string().required("Username wajib diisi"),
      password: Yup.string().min(6, "Minimal 6 karakter").required("Password wajib diisi"),
    });

    const toggleShow = () => {
      showPassword.value = !showPassword.value;
    };

    const onSubmit = async (values) => {
      isSubmitting.value = true;
      
      const Toast = Swal.mixin({
        toast: true,
        position: 'top-end',
        showConfirmButton: false,
        timer: 3000,
        timerProgressBar: true
      });

      try {
        // Login to FastAPI
        await authService.login(values.username, values.password);
        
        // Get user details
        const user = await authService.getCurrentUser();
        
        // Store in localStorage for portal
        localStorage.setItem("currentUser", JSON.stringify(user));

        router.push("/");
      } catch (error) {
        console.error("Login error:", error);
        let message = "Terjadi kesalahan saat login";
        
        if (error.response && error.response.status === 401) {
          message = "Username atau password salah!";
        } else if (error.response && error.response.data && error.response.data.detail) {
          message = error.response.data.detail;
        }

        Toast.fire({
          icon: 'error',
          title: message
        });
      } finally {
        isSubmitting.value = false;
      }
    };

    return {
      schema,
      onSubmit,
      showPassword,
      toggleShow,
      isSubmitting,
    };
  },
};
</script>

<style scoped>
.login-main-wrapper {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8faff;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  background: radial-gradient(circle at 50% 50%, #ffffff 0%, #edf2f7 100%);
}

.login-container {
  width: 100%;
  max-width: 420px;
  padding: 20px;
}

.login-card {
  background: #ffffff;
  padding: 40px;
  border-radius: 20px;
  border: none;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05) !important;
}

.minimal-input {
  border-radius: 10px;
  padding: 12px 15px;
  border: 1px solid #e2e8f0;
  background-color: #fcfcfd;
  font-size: 15px;
  transition: all 0.2s ease;
}

.minimal-input:focus {
  border-color: #059468;
  background-color: #fff;
  box-shadow: 0 0 0 4px rgba(5, 148, 104, 0.1);
}

.password-toggle-btn {
  position: absolute;
  right: 15px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  color: #94a3b8;
  z-index: 10;
  transition: color 0.2s;
}

.password-toggle-btn:hover {
  color: #64748b;
}

.btn-login-custom {
  background-color: #059468;
  color: white;
  border: none;
  border-radius: 10px;
  padding: 12px;
  font-size: 16px;
  transition: all 0.2s;
}

.btn-login-custom:hover:not(:disabled) {
  background-color: #047857; /* Deepen on hover */
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(5, 148, 104, 0.25);
  color: white;
}

.btn-login-custom:active {
  transform: translateY(0);
}



.error-msg {
  font-weight: 500;
  display: block;
}

@media (max-width: 480px) {
  .login-card {
    padding: 30px 20px;
  }
}
</style>
